import { useState, useEffect, useContext, useRef, forwardRef, useImperativeHandle } from 'react';
import { ConfirmationCard } from 'components/global/ConfirmationCard/ConfirmationCard';
import {
    Card,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Backdrop,
    IconButton,
    CircularProgress,
    Button
} from '@mui/material';
import { AivagamButton } from 'components/global/AivagamButton/AivagamButton';
import axios from 'axios';

import {
    Menu,
    Mic as MicIcon,
    Stop as StopIcon,
    CheckCircle as CheckCircleIcon,
    Lock as LockIcon,
    FolderOutlined,
    Close,
} from '@mui/icons-material';

import { useNavigate, ViewContext } from 'components/lib';
import Styles from './InterviewPracticePage.module.scss';
import { LoadingBackdrop } from 'components/global/LoadingBackdrop/LoadingBackdrop';
import { Bar, Doughnut } from 'react-chartjs-2';
import { Chart, BarElement, CategoryScale, LinearScale, ArcElement, Tooltip, Legend } from 'chart.js';
// import axiosRetry from 'axios-retry';
import { json, replace, useLocation } from 'react-router-dom';
import { InterviewResult } from '../InterviewResult/InterviewResult';
import { InterviewSections } from '../InterviewSections/InterviewSections';
import FeatherIcon from "feather-icons-react/build/FeatherIcon";
import { useMemo } from 'react';

// Register necessary chart elements
Chart.register(BarElement, CategoryScale, LinearScale, ArcElement, Tooltip, Legend);

export function InterviewPracticePage() {
    const viewContext = useContext(ViewContext);
    const navigate = useNavigate();

    const [isRecording, setIsRecording] = useState(false);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const streamRef = useRef(null);
    const [projectDialogOpen, setProjectDialogOpen] = useState(false);
    const [showConfirmCard, setShowConfirmCard] = useState(false);
    const [questionResults, setQuestionResults] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [apiError, setApiError] = useState(null);

    //UseStates that are actually being used
    const [loading, setLoading] = useState(true);
    const [showIntroCard, setShowIntroCard] = useState(true);
    const [menuVisible, setMenuVisible] = useState('false');
    const [questions, setQuestions] = useState([]);
    const [isFinished, setIsFinished] = useState(false)
    const [questionCounter, setQuestionCounter] = useState({
        communication: 0,
        reading: 0,
        technical: 0,
        situational: 0,
        performance: 0
    });
    const [currentSection, setCurrentSection] = useState('communication');
    const [showFinalResults, setShowFinalResults] = useState(false);
    const [sections, setSections] = useState([
        {
            id: 'communication',
            name: 'Communication',
            flag: 'current',
            questions: [],
            results: {},
        },
        {
            id: 'reading',
            name: 'Reading',
            flag: 'pending',
            questions: [],
            results: {},
        },
        {
            id: 'technical',
            name: 'Technical',
            flag: 'pending',
            questions: [],
            results: {},
        },
        {
            id: 'situational',
            name: 'Situational',
            flag: 'pending',
            questions: [],
            results: {},
        },
        {
            id: 'performance',
            name: 'Performance',
            flag: 'pending',
            questions: [],
            results: {},
        },
    ]);
    const [sectionCounter, setSectionCounter] = useState(0);
    const [sessionId, setSessionId] = useState('');
    const [showResultsDialog, setShowResultsDialog] = useState(false);
    const [audioUrl, setAudioUrl] = useState(null);
    const [hasResponded, setHasResponded] = useState(false);
    const audioPlayerRef = useRef(null);
    const [status, setStatus] = useState([{
        communication: 'pending',
        reading: 'pending',
        technical: 'pending',
        situational: 'pending',
        performance: 'pending',
    }]);
    const [isCompletedSection, setIsCompletedSection] = useState(false);
    const [showSaveAnswerConformCard, setShowSaveAnswerConformCard] = useState(false);
    const [showSaveAnswerCard, setSaveAnswerCard] = useState(true)

    useEffect(() => {
        if (sessionId) {
            localStorage.setItem('sessionId', sessionId);
        }
    }, [sessionId]);

    useEffect(() => {
        const completedList = sections.filter(section => section.flag === "completed");
        setIsFinished(completedList.length === sections.length - 1)
    }, [questionCounter])

    useEffect(() => {
        const runEffect = async () => {
            try {
                const sectionIdFromLocalStorage = JSON.parse(localStorage.getItem("sectionId"));

                if (sectionIdFromLocalStorage) {
                    // If user is resuming an old session → reset and redirect
                    localStorage.removeItem("questions");
                    localStorage.removeItem("sectionId");
                    navigate("/interview/topics");
                    return;
                }

                // Safe to generate questions
                await generateQuestions();
            } catch (err) {
                console.error("Error during trial attempt flow:", err.message);
                navigate("/oops");
            }
        };

        runEffect();
    }, [navigate])

    const generateQuestions = async () => {
        try {
            // Retrieve data from sessionStorage
            const topic = JSON.parse(sessionStorage.getItem('topic'));
            const subtopic = JSON.parse(sessionStorage.getItem('subtopic'));
            const profile = JSON.parse(sessionStorage.getItem('profileData')) || {};

            if (!topic || !subtopic) {
                navigate('/interview/topics');
            }

            const formData = {
                topic: topic.topic_name,
                topic_id: topic.id,
                subtopic: subtopic.subtopic,
                subtopic_id: subtopic.id,
                profile: profile,
            };

            const options = {
                method: 'POST',
                url: `/api/interview/practice/start`,
                headers: {
                    accept: 'application/json',
                },
                data: formData,
            };

            // Make the API request with retry logic automatically applied
            const response = await axios.request(options);
            const responseData = response.data;

            setSections((prevSect) =>
                prevSect.map((section) => ({
                    ...section,
                    questions: responseData.questions[section.id] || [],
                })),
            );

            const firstSectionId = 'communication'; // or prevSect[0]?.id
            const firstQuestions = responseData.questions[firstSectionId] || [];
            setQuestions(firstQuestions);
            setSectionCounter(0);
            setSessionId(responseData.session);
            setShowConfirmCard(true);
        } catch (err) {
            console.error('Error generating questions:', err);
            setApiError('Failed to load questions');
        } finally {
            setLoading(false);
        }
    };

    const handleStart = async () => {
        try {
            setShowIntroCard(false);

            const res = await axios.patch("/api/interview/user/trial/attempt");
            const { remainingSessions } = res.data;

            if (remainingSessions < 0) {
                // No attempts left → redirect
                navigate("/interview/topics");
                return;
            }

            if (questions.length === 0) {
                setLoading(true);
            } else {
                setLoading(false);
                if (!isCompletedSection) {
                    const generatedAudioUrl = await generateVoice(questions[0]?.question);
                    setAudioUrl(generatedAudioUrl);
                }
            }
        } catch (error) {
            console.error("❌ Error starting practice:", error.message);
            navigate('/oops');
        }
    };

    //Helper function to calculate average results for each section
    function averageScores(questionResult) {
        const result = questionResult.find(res => res.id === currentSection);
        const results = result?.questionResults;
        if (results) {
            if (results.length === 0) {
                return { metrics: {}, totalScore: 0 };
            }
            const averagedResults = {};
            // Loop through each key (score type)
            const keys = Object.keys(results[0]);
            keys.forEach((key) => {
                // Calculate the average for the key
                const average = results.reduce((sum, result) => sum + result[key], 0) / results.length;

                // Add the average to the result object
                averagedResults[key] = parseFloat(average.toFixed(1));
            });
            const totalScore =
                Object.keys(averagedResults).reduce((sum, key) => sum + averagedResults[key], 0) /
                Object.keys(averagedResults).length;
            return { metrics: averagedResults, totalScore: parseFloat(totalScore.toFixed(1)) };

        }



    }

    const moveToNextQuestion = async () => {
        // setIsSubmitting(true)
        setHasResponded(false);
        let currentSection = sections.find(section => section.flag === "current").id;
        setCurrentSection(currentSection);
        if (questionCounter[currentSection] < questions.length - 1) {
            // const nextQuestionIndex = questionCounter + 1;
            setQuestionCounter(prevCount => {
                return {
                    ...prevCount,
                    [currentSection]: prevCount[currentSection] + 1
                }
            });

            const nextQuestion = questions[questionCounter[currentSection] + 1]?.question;
            if (nextQuestion) {
                const generatedAudioUrl = await generateVoice(nextQuestion);
                setAudioUrl(generatedAudioUrl);
            }
        } else {
            const results = averageScores(questionResults);

            setSections((prevSections) => {
                const updatedSections = [...prevSections];
                updatedSections[sectionCounter] = {
                    ...updatedSections[sectionCounter],
                    results,
                };
                return updatedSections;
            });
            setShowResultsDialog(true);
        }
    };

    const readQuestion = async (nextSection, switchSection = false) => {
        const nextQuestion = switchSection ? nextSection?.questions[questionCounter[nextSection.id]]?.question : nextSection?.questions[0].question;
        if (nextQuestion) {
            const generatedAudioUrl = await generateVoice(nextQuestion);
            setAudioUrl(generatedAudioUrl);
        }
    }

    const updateSectionResult = async (nextSection, moveToPending = false) => {

        if (moveToPending) {
            const currentSectionIndex = sections.findIndex(section => section.id === nextSection.id);
            setSectionCounter(currentSectionIndex);
        }

        const parsedSectionResults = sections.reduce((acc, section) => {
            acc[section.id] = section.results;
            return acc;
        }, {});
        const options = {
            method: 'PATCH',
            url: `/api/interview/${sessionId}/results`,
            headers: {
                accept: 'application/json',
            },
            data: { results: parsedSectionResults },
        };

        await axios
            .request(options)
            .then(function (response) {
                setCurrentSection(nextSection.id);
                setQuestions(nextSection?.questions || []); // Set questions for the next section
                readQuestion(nextSection)
                setShowResultsDialog(false);
                setSections(prevSection =>
                    prevSection.map(section => {
                        if (section.id === currentSection) {
                            return { ...section, flag: "completed" }
                        } else if (section.id === nextSection.id) {
                            return { ...section, flag: "current" }
                        } else {
                            return section
                        }

                    })
                );
                setIsCompletedSection(false);
            })
            .catch((err) => {
                viewContext.notification.show(
                    'Error to updating status. Please restart the interview practice',
                    'error',
                    true,
                );
            });
    }

    const moveToNextPendingSection = (nextSectionIndex) => {
        let testRound = nextSectionIndex
        for (let index = testRound; index < sections.length; index++) {
            const isPending = sections.some(section => section.flag === "pending")
            if (isPending) {
                if (sections[index].flag === "pending") {
                    setSectionCounter(index);
                    // setHasResponded(false);
                    // setIsCompletedSection(true);
                    setCurrentSection(sections[index].id)
                    updateSectionResult(sections[index])
                    break;
                } else if (index === sections.length - 1) {
                    testRound = 0;
                }
            } else {
                setIsCompletedSection(false);
                setSections(prevSection => prevSection.map(section => {
                    return { ...section, flag: "completed" }
                }))
                setIsFinished(true)
            }

        }
    }
    const moveToNextSection = async () => {

        // set flag of current section to completed and next section to current
        const currentSectionIndex = sections.findIndex(section => section.flag === "current");

        const isNextSectionCompleted = sections[currentSectionIndex + 1]?.flag === "completed";

        if (isNextSectionCompleted) {
            setIsCompletedSection(true);
            moveToNextPendingSection(currentSectionIndex + 1)
        } else {
            if (sectionCounter < Object.keys(sections).length - 1) {
                if (sectionCounter === sections.length - 1) {
                    const isPending = sections.find(section => section.flag === "pending")
                    const nextSection = isPending
                    updateSectionResult(nextSection)
                } else {
                    const nextSection = sections[currentSectionIndex + 1]
                    setSectionCounter(prev => prev + 1);
                    updateSectionResult(nextSection)
                }
            } else { // if current section is last section
                const nextSection = sections.find(section => section.flag === "pending")

                if (nextSection) {
                    updateSectionResult(nextSection, true)
                } else {
                    setIsCompletedSection(true);
                    setSections(prevSection => prevSection.map(section => {
                        return { ...section, flag: "completed" }
                    }))
                    setIsFinished(true)
                }
            }
        }
        setHasResponded(false);

    };

    useEffect(() => {
        if (sessionId) {
            sessionStorage.setItem('sessionId', sessionId);
        }

    }, [sessionId]);

    //Function to handle cancel in confirmation card
    const handleCancel = () => {
        sessionStorage.clear();
        localStorage.removeItem('sessionId');
        navigate('/interview/topics');
    };

    const startRecording = async () => {
        try {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                console.warn('Recording is already in progress.');
                return;
            }

            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;

            mediaRecorderRef.current = new MediaRecorder(stream);
            audioChunksRef.current = [];

            mediaRecorderRef.current.ondataavailable = (event) => {
                audioChunksRef.current.push(event.data);
            };

            mediaRecorderRef.current.onstop = async () => {
                if (streamRef.current) {
                    streamRef.current.getTracks().forEach((track) => track.stop());
                    streamRef.current = null;
                }

                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });

                try {
                    setIsSubmitting(true);
                    // const currentSection = sections.find(section => section.flag === "current").id;

                    // const questionNumber = questionCounte
                    const formData = new FormData();
                    formData.append('audio', audioBlob, 'recording.wav');
                    const currentSection = sections[sectionCounter].id;
                    const questionCounterValue = questionCounter[currentSection];
                    formData.append('section', currentSection);
                    formData.append('question', questions[questionCounterValue]?.question || '');
                    formData.append('referenceText', questions[questionCounterValue]?.text || '');
                    const response = await axios.post('/api/interview/results', formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                    });
                    /*
                    storing results in following format
                [{
                    id:currentSection, (eg., communication, reading, technical, situational, performance)
                    questionResults:[{Q1},{Q2},{Q3},{Q4},{Q5}]
                }]
    
                */
                    setQuestionResults(prevResult => {
                        const updated = [...prevResult]
                        const pendingSecitonResults = updated.some(result => result.id === currentSection);
                        const pendingSectionIndex = updated.findIndex(result => result.id === currentSection);
                        if (pendingSecitonResults) {
                            updated.map(result => {
                                result.id === currentSection &&
                                    updated[pendingSectionIndex].questionResults.push(response?.data?.data);

                            })
                        } else {
                            updated.push({ id: currentSection, questionResults: [response?.data?.data] })
                        }
                        return updated;

                    })
                } catch (error) {
                    console.error('Error submitting audio:', error);
                    setApiError(error.response?.data?.message || 'Failed to submit audio');
                } finally {
                    setIsSubmitting(false);
                    setHasResponded(true);
                }
            };

            mediaRecorderRef.current.start();
            setIsRecording(true);
        } catch (error) {
            console.error('Error accessing microphone:', error);
            setApiError('Error accessing microphone. Please ensure you have granted permission.');
        }
    };

    // Modify the stopRecording function
    const stopRecording = (() => {
        let isStopped = false; // Prevent multiple stops

        return (source = 'Unknown') => {
            if (isStopped) {
                console.warn(`stopRecording was already called from: ${source}`);
                return; // Exit function if already stopped
            }
            isStopped = true;

            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                mediaRecorderRef.current.stop();
            }

            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => {
                    if (track.readyState === 'live') {
                        track.stop();
                    }
                });
                streamRef.current = null;
            }

            setIsRecording(false);
        };
    })();

    const toggleMenu = () => {
        setMenuVisible(!menuVisible);
    };

    const handleProjectDialogOpen = () => {
        setProjectDialogOpen(true);
    };

    const handleProjectDialogClose = () => {
        setProjectDialogOpen(false);
    };

    const handleFinish = () => {
        // mark as completed all the sections.
        setSections(prevSection => prevSection.map(section => {
            return { ...section, flag: "completed" }
        }))
        const parsedSectionResults = sections.reduce((acc, section) => {
            acc[section.id] = section.results;
            return acc;
        }, {});
        const options = {
            method: 'PATCH',
            url: `/api/interview/${sessionId}/results`,
            headers: {
                accept: 'application/json',
            },
            data: { results: parsedSectionResults, status: 'completed' },
        };

        axios
            .request(options)
            .then(function (response) {
                setShowResultsDialog(false);
                setShowFinalResults(true);
            })
            .catch((err) => {
                viewContext.notification.show(
                    'Error to updating results. Please restart the interview practice',
                    'error',
                    true,
                );
            });
    };

    const generateVoice = async (questionText) => {

        try {
            const formData = {
                text: questionText,
            };

            const options = {
                method: 'POST',
                url: `/api/interview/practice/speech/generate`,
                headers: {
                    accept: 'application/json',
                },
                data: formData,
                responseType: 'blob',
            };
            const response = await axios.request(options);

            const audioUrl = URL.createObjectURL(response.data);
            setTimeout(() => {
                URL.revokeObjectURL(audioUrl);
            }, 900000);

            return audioUrl;
        } catch (err) {
            console.error('Error occurred:', err?.response?.status || err.message);
            navigate('/oops');
            return null;
        }
    };

    const handleQuestionAudioReplay = () => {
        if (audioPlayerRef.current) {
            audioPlayerRef.current.replayAudio();
        }
    };


    const handleChangeSection = (id) => {
        if (!hasResponded) {
            setCurrentSection(id);
            const isCompleted = sections.some(
                section => section.id === id && (section.flag === "completed" || section.flag === "cc")
            );

            setIsCompletedSection(isCompleted);
            const selectedSection = sections.find(section => section.id === id);
            const currentIndex = sections.findIndex(section => section.id === id);
            setSectionCounter(currentIndex)
            if (selectedSection) {
                setQuestions(selectedSection.questions);

                setSections(prevSections =>
                    prevSections.map(eachSec => {
                        if (eachSec.id === id) {
                            if (eachSec.flag === "completed" || eachSec.flag === "cc") {
                                return { ...eachSec, flag: 'cc' };
                            } else {
                                return eachSec.flag === "completed" || eachSec.flag === "cc"
                                    ? { ...eachSec, flag: 'completed' }
                                    : { ...eachSec, flag: "current" };
                            }
                        } else {
                            return eachSec.flag === "completed" || eachSec.flag === "cc"
                                ? { ...eachSec, flag: "completed" }
                                : { ...eachSec, flag: "pending" };
                        }
                    })
                );
            }
            //if select completed section won't read question.
            !isCompleted && readQuestion(selectedSection, true);
        } else {
            setShowSaveAnswerConformCard(true)
        }
    };
    const handleHome = () => {
        localStorage.removeItem('sessionId');
        navigate('/navigate')
    }

    const handleAnswerNotSaved = () => {
        setShowSaveAnswerConformCard(false)
    }
    return (
        <>
            {showConfirmCard && (
                <ConfirmationCard
                    title="Start Interview"
                    body="Let's start the interview practice"
                    showCard={showIntroCard}
                    setShowCard={setShowIntroCard}
                    handleConfirm={handleStart}
                    handleCancel={handleCancel}
                />
            )}
            {showSaveAnswerConformCard && (
                <ConfirmationCard
                    title="Answer not saved"
                    body="Your answer is incomplete. Please submit it before proceeding to the next section."
                    showCard={showSaveAnswerCard}
                    setShowCard={setSaveAnswerCard}
                    handleConfirm={handleAnswerNotSaved}
                />
            )}
            {loading ? (
                <LoadingBackdrop isOpen={loading} />
            ) : (<>
                {showFinalResults && <div className={Styles.backBtnContainer}>
                    <button type="button" className={Styles.homeButton}
                        onClick={handleHome}><FeatherIcon size="1em" icon="home" className={Styles.homeIcon} />Home
                    </button>
                    <h2 className={Styles.resultTitle}>Interview Practice Results</h2>
                </div>}
                <div className={Styles.pageContainer}>

                    <div className={`${Styles.leftSideMenu} ${menuVisible ? Styles.visible : Styles.hidden}`}>
                        <div className={Styles.backBtnContainer}>
                            <Button
                                sx={{
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                    textTransform: 'none',
                                    backgroundColor: 'transparent',
                                    '&:hover':{
                                        backgroundColor: 'transparent',
                                        
                                    }
                                }}
                                onClick={() => navigate('/interview/topics')}
                            >
                                {'< Back'}
                            </Button>

                        </div>
                        <ul className={Styles.menuList}>
                            {sections.map((section, index) =>

                            (<InterviewSections
                                key={index}
                                section={section}
                                handleChangeSection={handleChangeSection} />)

                            )}
                        </ul>
                    </div>
                    {showFinalResults ? <InterviewResult sessionId={sessionId} /> :
                        <div className={`${Styles.rightSideContent} ${menuVisible ? Styles.fullScreen : ''}`}>
                            {/* Rest of your right-side content */}
                            <div className={Styles.contentContainer}>
                                <nav className={Styles.headerNav}>
                                    <AivagamButton
                                        buttonText={<Menu fontSize="small" />}
                                        onClick={toggleMenu}
                                        type="action"
                                        icon={true}
                                        classStyles={{
                                            width: '3em',
                                            height: '3em',
                                            boxShadow: '0 3px 6px rgba(0, 0, 0, 0.3)',
                                        }}
                                        aria-label="menu"
                                    />
                                    <p className={Styles.questionTitle}>
                                        {questions[questionCounter[currentSection]]?.question ? `QUESTION: ${questionCounter[currentSection] + 1}/${sections[sectionCounter].questions?.length}` : ''}
                                    </p>
                                </nav>
                                <div>
                                    <Backdrop
                                        sx={{ color: '#000', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                                        open={showResultsDialog}
                                        invisible={!showResultsDialog}
                                    >
                                        <ResultsPane
                                            results={averageScores(questionResults)}
                                            moveToNextSection={moveToNextSection}
                                            setShowResultsDialog={setShowResultsDialog}
                                            handleFinish={handleFinish}
                                            isFinished={isFinished}
                                        />
                                    </Backdrop>
                                </div>
                                <div className={Styles.mainContent}>
                                    <div className={Styles.questionSection}>
                                        <Card className={Styles.questionCard}>
                                            <div className={Styles.cardContent}>
                                                <p className={Styles.questionText}>
                                                    {questions[questionCounter[currentSection]]?.question
                                                        ? questions[questionCounter[currentSection]]?.question
                                                        : 'Your Question will appear here'}
                                                </p>
                                                {questions[questionCounter[currentSection]]?.text && (
                                                    <p className={Styles.questionReferenceText}>{questions[questionCounter[currentSection]]?.text}</p>
                                                )}
                                                <div className={Styles.recordingSection}>
                                                    <div className={Styles.audioVisualizer}>
                                                        {isRecording && mediaRecorderRef.current.state === 'recording' ? (
                                                            <span className={Styles.recordingText}>
                                                                <p className={Styles.recordingTextAnimation}></p>
                                                            </span>
                                                        ) : isSubmitting ? (
                                                            <span className={Styles.recordingText}>Processing Response...</span>
                                                        ) : (
                                                            <span className={Styles.recordingText}>Click to Record Response</span>
                                                        )}
                                                    </div>

                                                    <div className={Styles.micButtonContainer}>
                                                        {isSubmitting ? (
                                                            <div>
                                                                <CircularProgress fontSize="medium" />
                                                            </div>
                                                        ) : (
                                                            <AivagamButton
                                                                buttonText={isRecording ? <StopIcon /> : <MicIcon />}
                                                                onClick={isRecording ? stopRecording : startRecording}
                                                                type={isRecording ? 'destruct' : 'confirm'}
                                                                icon={true}
                                                                disabled={isSubmitting || hasResponded || isCompletedSection}
                                                                classStyles={{
                                                                    width: '3.5em',
                                                                    height: '3.5em',
                                                                    boxShadow: '0 3px 6px rgba(0, 0, 0, 0.3)',
                                                                }}
                                                                aria-label={isRecording ? 'stop recording' : 'start recording'}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </Card>
                                    </div>

                                    <div className={Styles.avatarSection}>
                                        <Card className={Styles.avatarCard}>
                                            <div className={Styles.cardContent}>
                                                <div className={Styles.avatarPlaceholder}>
                                                    <AvatarAudioPlayer ref={audioPlayerRef} audioUrl={audioUrl} />
                                                </div>
                                            </div>

                                            <div className={Styles.navigationControls}>
                                                <div className={Styles.navigationButtons}>
                                                    <AivagamButton
                                                        buttonText={'Play Again'}
                                                        disabled={hasResponded || isCompletedSection}
                                                        onClick={handleQuestionAudioReplay}
                                                        type="confirm"
                                                        classStyles={{
                                                            width: '120px',
                                                            height: '40px',
                                                            boxShadow: '0 3px 6px rgba(0, 0, 0, 0.3)',
                                                        }}
                                                    />
                                                    <AivagamButton
                                                        buttonText={questionCounter[currentSection] === questions.length - 1 ? 'Show Results' : 'Next Question'}
                                                        onClick={moveToNextQuestion}
                                                        disabled={questionCounter[currentSection] > questions.length - 1 || isSubmitting || !hasResponded || isCompletedSection}
                                                        type="action"
                                                        classStyles={{
                                                            width: '120px',
                                                            height: '40px',
                                                            boxShadow: '0 3px 6px rgba(0, 0, 0, 0.3)',
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </Card>
                                    </div>
                                </div>
                            </div>
                        </div>}
                </div>
            </>
            )}
        </>
    );
}

const ResultsPane = ({ results, moveToNextSection, setShowResultsDialog, isFinished, handleFinish }) => {
    const metrics = results?.metrics || {};
    const totalScore = results?.totalScore || 0;

    const transformMetricsToChartData = (results) => {
        const labels = Object.keys(results).map(eachLabel => eachLabel[0].toUpperCase() + eachLabel.slice(1));
        const values = Object.values(results);

        return {
            labels,
            datasets: [
                {
                    label: `Score (${totalScore}%)`,
                    data: values,
                    backgroundColor: '#3974c1',
                    borderColor: '#3974c1',
                    borderWidth: 1,
                },
            ],
        };
    };

    const chartData = transformMetricsToChartData(metrics);

    const donutChartData = {
        labels: ['', ''],
        datasets: [
            {
                data: [totalScore, 100 - totalScore],
                backgroundColor: ['#3974c1', 'lightgrey'],
            },
        ],
    };

    return (
        <div
            id="resultsContainer"
            style={{
                width: '70vw',
                height: '80vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'white',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
            }}
        >
            <div
                id="chartContainer"
                style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '2em',
                }}
            >
                <div style={{ width: '40%', height: '60%' }}>
                    <Bar
                        data={chartData}
                        options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            scales: {
                                y: {
                                    beginAtZero: true,
                                    max: 100,
                                    grid: {
                                        display: false,
                                        drawBorder: false,
                                    },
                                },
                                x: {
                                    grid: {
                                        display: false,
                                    },
                                },
                            },
                            plugins: {
                                legend: {
                                    display: true,
                                },
                            },
                        }}
                    />
                </div>
                <div style={{ width: '40%', height: '60%' }}>
                    <Doughnut
                        data={donutChartData}
                        options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            cutout: '60%',
                            plugins: {
                                legend: {
                                    display: false,
                                },
                                tooltip: {
                                    enabled: false,
                                },
                                title: {
                                    display: true,
                                    text: `Overall Score: ${totalScore}%`, // The title text
                                    font: {
                                        size: 18,
                                        weight: 'bold',
                                    },
                                    color: '#333',
                                    padding: {
                                        bottom: 10,
                                    },
                                },
                            },
                        }}
                    />
                </div>
            </div>
            <div
                id="sectionButtonContainer"
                style={{
                    width: '100%',
                    height: '12vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    padding: '1.5em',
                }}
            >
                <AivagamButton
                    buttonText={isFinished ? 'Finish' : 'Next Section'}
                    onClick={isFinished ? handleFinish : moveToNextSection}
                    type="action"
                    classStyles={{
                        width: '120px',
                        height: '40px',
                        boxShadow: '0 3px 6px rgba(0, 0, 0, 0.3)',
                    }}
                />
            </div>
        </div>
    );
};

const AvatarAudioPlayer = forwardRef(({ audioUrl }, ref) => {
    const [isAiAudioPlaying, setIsAiAudioPlaying] = useState(false);
    const audioRef = useRef(null);

    useEffect(() => {
        setIsAiAudioPlaying(false);
    }, [audioUrl]);

    const handleAudioPlay = () => {
        setIsAiAudioPlaying(true);
    };

    const handleAudioEnded = () => {
        setIsAiAudioPlaying(false);
    };

    const handleAudioPause = () => {
        setIsAiAudioPlaying(false);
    };

    // Expose replay function to parent via ref
    useImperativeHandle(ref, () => ({
        replayAudio: () => {
            if (audioRef.current) {
                audioRef.current.currentTime = 0; // Reset to beginning
                setIsAiAudioPlaying(true);
                audioRef.current.play();
            }
        },
    }));

    return (
        <div className={Styles.avatarPlaceholder}>
            {/* Show animated GIF only while audio is playing */}
            <img
                src={
                    isAiAudioPlaying
                        ? `${process.env.REACT_APP_GLOBAL_MEDIA_URL}/topos-icons/global/avatar_audio.gif`
                        : `${process.env.REACT_APP_GLOBAL_MEDIA_URL}/topos-icons/global/avatar_no_audio.gif`
                }
                alt="Avatar"
                className={Styles.avatarImage}
            />

            <audio
                ref={audioRef}
                autoPlay
                preload="auto"
                src={audioUrl}
                onPlay={handleAudioPlay}
                onEnded={handleAudioEnded}
                onPause={handleAudioPause}
            />
        </div>
    );
});
